import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Modal, SingleSelect } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { getSlotTypeTranslation } from '@/helpers';
import { MusicIcon, PhotoIcon, PlusIcon, SpeechIcon, VideoIcon } from '@/resources';
import { Playlists } from '@/resources/icons/sidebar';
import { useGetAllCeremonySlotTypes } from '@/services';
import { CeremonyTemplateSlot, SlotType } from '@/types';

interface AddCeremonyTemplateSlotProps {
  organizationId: number;
  slots: CeremonyTemplateSlot[];
  onSlotsChange: (newSlots: CeremonyTemplateSlot[]) => void;
  hasCeremonySetupAccess: boolean;
}

export const AddEventTemplateSlot = ({
  organizationId,
  slots,
  onSlotsChange,
  hasCeremonySetupAccess
}: AddCeremonyTemplateSlotProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedSlotTypeId, setSelectedSlotTypeId] = useState<number>(-1);
  const [t] = useTranslation('common');

  const { data: slotTypes, isLoading: isLoadingSlotTypes } = useGetAllCeremonySlotTypes(
    organizationId,
    hasCeremonySetupAccess
  );
  const isLoading = hasCeremonySetupAccess && isLoadingSlotTypes;

  const addSlotToList = (slotTypeId: number) => {
    const selectedSlotType = slotTypes?.data?.items.find(
      (type: SlotType) => type.id === slotTypeId
    );

    if (!selectedSlotType) {
      return;
    }

    const newSlot: CeremonyTemplateSlot = {
      id: 0,
      order: slots.length + 1,
      slotTypeId: selectedSlotType.id,
      slotType: selectedSlotType,
      ceremonyTemplateSlotEvents: []
    };

    const updatedSlots = [...slots, newSlot];
    onSlotsChange(updatedSlots);

    setIsModalOpen(false);
    setSelectedSlotTypeId(-1);
  };

  const getOptions = () => {
    return slotTypes?.data?.items.map((type: SlotType) => {
      let icon = null;
      switch (type.assetType) {
        case 'TributeVideo':
        case 'Video':
          icon = <VideoIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'CoverPhoto':
        case 'Image':
          icon = <PhotoIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Audio':
          icon = <MusicIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Text':
          icon = <SpeechIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Playlist':
          icon = <Playlists style={{ color: type.iconColor }} className='size-5' />;
          break;
      }

      return {
        value: type.id,
        label: getSlotTypeTranslation(type.id),
        icon: icon
      };
    }) as { value: number; label: string; icon?: JSX.Element }[];
  };

  const getSelectedValue = () => {
    if (selectedSlotTypeId <= 0) {
      return null;
    }
    return getOptions()?.find((option) => option.value === selectedSlotTypeId) || null;
  };

  const handleSubmit = () => {
    if (selectedSlotTypeId > 0) {
      addSlotToList(selectedSlotTypeId);
    }
  };

  const handleModalOpen = () => {
    if (!hasCeremonySetupAccess) {
      return;
    }
    setSelectedSlotTypeId(-1);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSlotTypeId(-1);
  };

  return (
    <div
      className={clsx(
        'flex aspect-square w-full items-center justify-center rounded-2xl border border-primary-blue',
        hasCeremonySetupAccess ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
      )}
      onClick={handleModalOpen}>
      <PlusIcon className='size-9 text-primary-blue' />
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        height='300px'
        modalClasses='w-full md:w-[600px] h-[300px]'>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className='size-full px-6'>
            <Label text={t('addSlot')} type={LABEL_TYPE.H2} className='mb-6 font-semibold' />
            <div className='flex flex-col gap-y-6'>
              <SingleSelect
                placeholder={t('selectType')}
                id='slotTypeId'
                label={t('type')}
                options={getOptions()}
                value={getSelectedValue()}
                handleChange={(slotType) => setSelectedSlotTypeId(Number(slotType?.value) ?? -1)}
                isRequired
                textColor='var(--color-secondary-gray)'
              />
              <div className='flex w-full justify-end'>
                <Button
                  type='button'
                  disabled={selectedSlotTypeId < 1}
                  handleOnClick={handleSubmit}>
                  {t('save')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
